import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AuthenticationError,
  ErrorType,
  GetVenueResponse,
  authenticateToConnect,
  isConnectVenueReady,
  v1VenueCreateVenue,
  v1VenueGetVenue,
  v1VenueShouldVerifyEmail,
  v1VerificationSendVerificationCode,
  v1VerificationVerifyCode,
} from '../api-client';
import { validateBusinessName } from '../api-client/validate-business-name';
import { getGaSessionId, getGclid } from '../helpers/attribution';
import { isBusinessNameTooLong } from '../helpers';
import { getGTMLegacySignupEvent, getGTMSignupEvent } from '../features/gtm';
import {
  SegmentEventType,
  UserType,
  useSegmentContext,
} from '../features/segment-context';
import { useCaptchaContext } from '../features/captcha-context';
import { CODE_LENGTH } from '../steps-contents';
import {
  Deregister,
  FormContextData,
  RegisterValidator,
  Step,
  Validator,
} from './types';
import { getStepConfiguration } from './step-configuration';
import { goToTwBusiness, redirectToConnect } from './redirect-utils';

type ErrorState = {
  error: unknown;
  source: 'AboutSignUp' | 'AddInfo' | 'AddServices' | 'AddBusinessType';
};
type FormErrorState = { type: ErrorType; key: string }[];
type StepMachineState = null | GetVenueResponse | ErrorState | FormErrorState;

export function isRedirectState(
  state: StepMachineState,
): state is GetVenueResponse {
  return !!state && 'redirectURL' in state;
}

export function isErrorState(state: StepMachineState): state is ErrorState {
  return !!state && 'error' in state;
}

export function isFormErrorState(
  state: StepMachineState,
): state is FormErrorState {
  return !!state && Array.isArray(state);
}

function stepToSegmentStep(
  step: Step,
): 'AboutSignUp' | 'AddInfo' | 'AddServices' | 'AddBusinessType' | 'unknown' {
  switch (step) {
    case Step.STEP_ONE_SEARCH:
    case Step.STEP_ONE_FORM:
      return 'AddInfo';
    case Step.STEP_TWO_FORM:
      return 'AddServices';
    case Step.STEP_THREE_FORM:
      return 'AddBusinessType';
    case Step.STEP_FOUR_FORM:
      return 'AboutSignUp';
    case Step.STEP_FINAL:
    case Step.STEP_FINAL_REDIRECT_LOGIN:
    case Step.STEP_ERROR:
    default:
      return 'unknown';
  }
}

function getActionFromAddInfoMode(
  addInfoMode?: 'manual' | 'automatic',
): 'search' | 'manual' | 'skip' {
  if (addInfoMode === 'manual') {
    return 'manual';
  }
  if (addInfoMode === 'automatic') {
    return 'search';
  }
  return 'skip';
}

// The business name is used as both the supplier name and the venue name. A taken name isn't fatal:
// creation appends the city to make it unique, so it's only truly unavailable when "<name>, <city>"
// is also taken. This mirrors the retry the BFF used to do at submit time, keeping the feedback the
// user gets here consistent with what creation would actually do.
//
// Each `validateBusinessName` call needs its own captcha token (reCAPTCHA v3 tokens are one-shot).
//
// Fail-open on any error (captcha rejection, network blip, missing reCAPTCHA script). The Next
// button's job here is a UX hint; submission later runs an authoritative check with its own
// captcha, so blocking the user here on a transient failure costs us conversion for no real gain.
async function isBusinessNameAvailable(
  data: FormContextData,
  executeCaptcha: (action: string) => Promise<string>,
): Promise<boolean> {
  try {
    const name = data.businessName.trim();
    const { countryCode, city } = data.address;
    const firstToken = await executeCaptcha(
      'self_signup/validate_business_name',
    );
    const first = await validateBusinessName(name, countryCode, firstToken);
    let supplierOk = first.supplier.canCreate;
    let venueOk = first.venue.canCreate;
    if (!supplierOk || !venueOk) {
      const nameWithCity = `${name}, ${city}`;
      if (isBusinessNameTooLong(nameWithCity)) {
        return false;
      }
      const retryToken = await executeCaptcha(
        'self_signup/validate_business_name',
      );
      const withCity = await validateBusinessName(
        nameWithCity,
        countryCode,
        retryToken,
      );
      supplierOk = supplierOk || withCity.supplier.canCreate;
      venueOk = venueOk || withCity.venue.canCreate;
    }
    return supplierOk && venueOk;
  } catch {
    return true;
  }
}

export type StepMachine = {
  step: Step;
  state: StepMachineState;
  loading: boolean;
  onNext?: (params?: { addInfoMode?: 'manual' | 'automatic' }) => Promise<void>;
  onPrevious?: () => Promise<void>;
  registerValidator: RegisterValidator;
  clearState: () => void;
};

const READINESS_POLLING_RATE = 1_000;
const READINESS_MAX_ATTEMPTS = 15;

// The job reporting SUCCESS only means the creation code ran; the new venue's
// extranet authorisation can lag a moment behind becoming queryable. Once
// authenticated, probe it before redirecting so Connect doesn't load into a 403
// blank page (CPG-176). Best-effort: if it never reports ready we redirect anyway
// so the user isn't trapped, and Connect's own retry handles the remainder.
const waitForConnectReady = async () => {
  for (let attempt = 0; attempt < READINESS_MAX_ATTEMPTS; attempt += 1) {
    if (await isConnectVenueReady()) {
      return;
    }
    await new Promise(resolve => setTimeout(resolve, READINESS_POLLING_RATE));
  }
};

const getLpPartners = () => {
  const cookie = document?.cookie;
  if (!cookie && typeof cookie === 'string') {
    return;
  }

  const LP_PARTNERS_PREFIX = 'lp_partners=';
  const lpPartners = cookie
    .split('; ')
    .find(entry => entry.startsWith(LP_PARTNERS_PREFIX))
    ?.slice(LP_PARTNERS_PREFIX.length);

  return decodeURIComponent(lpPartners || '');
};

const getOptanonConsent = () => {
  const cookie = document?.cookie;
  if (!cookie && typeof cookie === 'string') {
    return 0;
  }

  const optanonConsent = cookie
    .split('; ')
    .find(row => row.startsWith('OptanonConsent='));

  if (optanonConsent) {
    const CONSENT_PARAM_NAME = 'C0004:';
    const decodedCookieValue = decodeURIComponent(optanonConsent);
    const consentPosition = decodedCookieValue.indexOf(CONSENT_PARAM_NAME);
    const consentValue =
      decodedCookieValue[consentPosition + CONSENT_PARAM_NAME.length];

    return parseInt(consentValue);
  }

  return 0;
};

export function useStepMachine(
  localStorageStep: Step,
  data: FormContextData,
): StepMachine {
  const { executeCaptcha } = useCaptchaContext();
  const [step, setStep] = useState<Step>(localStorageStep);
  const { previousKey } = getStepConfiguration(step);
  const [state, setState] = useState<StepMachineState>(null);
  const [loading, setLoading] = useState(false);
  const [validators, setValidators] = useState<Validator[]>([]);
  // addInfoMode is passed in onNext on STEP_ONE_SEARCH but event is trigger at STEP_ONE_FORM
  const addInfoModeRef = useRef<'manual' | 'automatic'>();
  const { trackEvent, identify, trackVenueCreated } = useSegmentContext();
  const { t } = useTranslation('stepError');

  const signUp = useCallback(
    async (userType: UserType) => {
      trackEvent(SegmentEventType.ABOUT_SIGN_UP, {
        step: 'AboutSignUp',
        marketing_opt_in: data.acceptMarketing || false,
      });

      const signupGTMData = await getGTMSignupEvent({ email: data.email });
      window.dataLayer?.push(signupGTMData);
      window.dataLayer?.push(getGTMLegacySignupEvent());

      setState(null);
      setLoading(true);

      try {
        const token = await executeCaptcha('self_signup/sign_up');

        const { errors, venueId, communityAccountId } =
          await v1VenueCreateVenue({
            businessName: data.businessName.trim(),
            businessType: data.businessType!, // should be enforced by form validation
            dedicatedSpace: !data.dedicatedSpace,
            businessSegments: data.businessSegments,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            password: data.password,
            phoneNumber: data.phoneNumber,
            address: data.address,
            acceptsMarketing: data.acceptMarketing,
            verificationCode: data.verificationCode,
            placeId: data.placeId,
            leadId: data.leadId,
            opportunityId: data.opportunityId,
            gaSessionId: getGaSessionId(),
            gclid: getGclid(),
            partnersURL: getLpPartners(),
            adConsent: getOptanonConsent(),
            token,
          });
        if (errors.length) {
          setState(errors);
          trackEvent(SegmentEventType.ERROR_MESSAGE_SHOWN, {
            step: 'AboutSignUp',
            errors: errors.map(e => `${e.key} ${e.type}`),
            business_name: data.businessName,
            address: `${data.address.street}, ${data.address.city}, ${data.address.postalCode}, ${data.address.countryCode}`,
          });
          // In case of errors on the first form, go back to step one
          if (
            errors.map(e => e.key).includes('businessName') ||
            errors.map(e => e.key).includes('address')
          ) {
            setStep(Step.STEP_ONE_FORM);
            return;
          }
          // If the verification code is invalid (expired), go to relevant step
          if (errors.map(e => e.key).includes('verificationCode')) {
            setStep(Step.STEP_EMAIL_VERIFICATION);
          } else {
            setStep(Step.STEP_FOUR_FORM);
          }
          return;
        }
        identify({ ...data, communityAccountId });
        trackVenueCreated({
          venueId,
          userType,
          countryCode: data.address.countryCode,
        });
        setStep(Step.STEP_FINAL);
        setLoading(false);
        {
          // Venue is created (Java sign-up is synchronous). The Turnstile token was
          // collected on STEP_FOUR_FORM while the user filled in their details and
          // should still be valid in most cases. If it has since expired — or the
          // user went through email verification — we fall back to STEP_TURNSTILE
          // so they can obtain a fresh one without seeing a misleading error.
          // The venue was already created; this retry only affects authentication.
          //
          // Guard explicitly rather than using a non-null assertion so that any
          // future call path that skips STEP_FOUR_FORM fails loudly here rather
          // than passing undefined to the auth endpoint.
          if (!data.turnstileToken) {
            // Track fallback so we can monitor rate in Datadog via Segment.
            trackEvent(SegmentEventType.TURNSTILE_FALLBACK_TRIGGERED, {
              reason: 'token_absent',
            });
            setStep(Step.STEP_TURNSTILE);
            return;
          }
          try {
            await authenticateToConnect({
              email: data.email,
              password: data.password,
              turnstileToken: data.turnstileToken,
            });
            await waitForConnectReady();
            redirectToConnect();
          } catch (authError) {
            // Distinguish between a token rejection (401/403 — retry makes sense)
            // and any other failure (network error, 5xx — retrying Turnstile will
            // not help and would create an infinite loop). Only navigate back to
            // STEP_TURNSTILE for token errors; surface everything else as a
            // genuine error so the user is not stuck retrying indefinitely.
            const isTokenError =
              authError instanceof AuthenticationError &&
              (authError.status === 401 || authError.status === 403);
            if (isTokenError) {
              // Token was rejected — most likely expired during a slow job.
              // Navigate back to STEP_TURNSTILE for a fresh one.
              // The venue is already created; this retry only affects auth.
              // Surface the error so the widget UI can show feedback.
              trackEvent(SegmentEventType.TURNSTILE_FALLBACK_TRIGGERED, {
                reason: 'token_expired',
              });
              setState([{ key: 'turnstileToken', type: ErrorType.INVALID }]);
              setStep(Step.STEP_TURNSTILE);
            } else {
              setState({
                error: t('accountCreation'),
                source: 'AboutSignUp',
              });
              setStep(Step.STEP_ERROR);
            }
          }
        }
      } catch (error) {
        setState({
          error,
          source: 'AboutSignUp',
        });
        setStep(Step.STEP_ERROR);
      } finally {
        setLoading(false);
      }
    },
    [data, executeCaptcha, identify, trackEvent, trackVenueCreated, t],
  );

  const shouldVerifyEmail = useCallback(async () => {
    try {
      const token = await executeCaptcha('self_signup/should_verify_email');
      const { shouldVerifyEmail } = await v1VenueShouldVerifyEmail({
        email: data.email,
        token,
      });
      return shouldVerifyEmail;
    } catch (error) {
      setState({
        error,
        source: 'AboutSignUp',
      });
      setStep(Step.STEP_ERROR);
    }
  }, [data, executeCaptcha]);

  const findExisting = useCallback(async () => {
    try {
      const token = await executeCaptcha('self_signup/get_venue');
      const response = await v1VenueGetVenue({
        email: data.email,
        token,
      });

      return response.redirectURL;
    } catch (error) {
      setState({
        error,
        source: 'AboutSignUp',
      });
      setStep(Step.STEP_ERROR);
    }
  }, [data, executeCaptcha]);

  const sendVerificationCode = useCallback(async () => {
    try {
      const token = await executeCaptcha('self_signup/send_verification_code');
      await v1VerificationSendVerificationCode({
        firstName: data.firstName,
        lastName: data.lastName,
        businessName: data.businessName.trim(),
        email: data.email,
        language: navigator.language || data.address.countryCode,
        token,
      });
    } catch (error) {
      setState({
        error,
        source: 'AboutSignUp',
      });
      setStep(Step.STEP_ERROR);
    }
  }, [data, executeCaptcha]);

  const verifyAndSignup = useCallback(async () => {
    const needsVerification = await shouldVerifyEmail();
    if (needsVerification) {
      await sendVerificationCode();
      setStep(Step.STEP_EMAIL_VERIFICATION);
    } else {
      // No existing active account for this email → brand-new user.
      await signUp(UserType.NEW);
    }
  }, [shouldVerifyEmail, signUp, sendVerificationCode]);

  const isValidVerificationCode = useCallback(
    async (code: string) => {
      const token = await executeCaptcha('self_signup/verify_code');
      const response = await v1VerificationVerifyCode({
        code,
        email: data.email,
        token,
      });
      return response.success;
    },
    [data, executeCaptcha],
  );

  const onNext = useCallback(
    async (params?: { addInfoMode?: 'manual' | 'automatic' }) => {
      setLoading(true);
      try {
        const validationResults = await Promise.all(
          validators.map(v => v(data)),
        );
        const invalidResults = validationResults.filter(
          (v): v is { error: string } => !!v.error,
        );
        if (invalidResults.length) {
          trackEvent(SegmentEventType.ERROR_MESSAGE_SHOWN, {
            step: stepToSegmentStep(step),
            errors: invalidResults.map(i => i.error),
          });
          return;
        }

        if (params?.addInfoMode) {
          addInfoModeRef.current = params?.addInfoMode;
        }

        switch (step) {
          case Step.STEP_CONTEXT: {
            trackEvent(SegmentEventType.CONTEXT_STEP_CLICK, {
              step: 'ContextStep',
            });
            setStep(Step.STEP_ONE_SEARCH);
            break;
          }
          case Step.STEP_ONE_SEARCH: {
            trackEvent(SegmentEventType.BUSINESS_SEARCH, {
              step: 'AddInfo',
              action: getActionFromAddInfoMode(params?.addInfoMode),
            });
            setStep(Step.STEP_ONE_FORM);
            break;
          }
          case Step.STEP_ONE_FORM: {
            // Re-check availability on Next: onBlur alone let a user change the name and click
            // through without seeing the error. Blocks navigation if the name is taken.
            if (!(await isBusinessNameAvailable(data, executeCaptcha))) {
              setState([
                { type: ErrorType.ALREADY_EXISTS, key: 'businessName' },
              ]);
              trackEvent(SegmentEventType.ERROR_MESSAGE_SHOWN, {
                step: stepToSegmentStep(step),
                errors: ['businessName'],
              });
              return;
            }
            setState(null);
            trackEvent(SegmentEventType.ADD_INFO, {
              step: 'AddInfo',
              mode: params?.addInfoMode || addInfoModeRef.current || 'manual',
            });
            setStep(Step.STEP_TWO_FORM);
            break;
          }
          case Step.STEP_TWO_FORM:
            trackEvent(SegmentEventType.ADD_SERVICES, {
              step: 'AddServices',
              services_selection: data.businessSegments,
            });
            setStep(Step.STEP_THREE_FORM);
            break;
          case Step.STEP_THREE_FORM:
            trackEvent(SegmentEventType.ADD_BUSINESS_TYPE, {
              step: 'AddBusinessType',
              business_selection: data.businessType!,
            });
            setStep(Step.STEP_FOUR_FORM);
            break;
          case Step.STEP_FOUR_FORM: {
            const existing = await findExisting();
            if (existing) {
              setState({
                redirectURL: existing,
              });
              setStep(Step.STEP_FINAL_REDIRECT_LOGIN);
              return;
            }
            await verifyAndSignup();
            break;
          }
          case Step.STEP_FINAL_REDIRECT_LOGIN: {
            trackEvent(SegmentEventType.CONTINUE_WITH_SIGNUP, {
              step: 'LoginRedirect',
            });
            await verifyAndSignup();
            break;
          }
          case Step.STEP_EMAIL_VERIFICATION: {
            if (!data.verificationCode) {
              return;
            }
            const isValidCode = await isValidVerificationCode(
              data.verificationCode,
            );
            if (!isValidCode) {
              setState([
                {
                  key: 'verificationCode',
                  type: ErrorType.INVALID,
                },
              ]);
              trackEvent(SegmentEventType.ERROR_MESSAGE_SHOWN, {
                step: 'VerificationCode',
                errors: ['Invalid verification code'],
              });
              return;
            } else {
              // Email verification is only reached when an active account
              // already existed for this email → existing B2C user.
              await signUp(UserType.EXISTING_B2C);
            }
            break;
          }
          case Step.STEP_FINAL:
            goToTwBusiness();
            break;
          case Step.STEP_TURNSTILE: {
            // Fallback path — only reached when the token collected on
            // STEP_FOUR_FORM was absent or expired by the time the job
            // completed. The widget on this screen has issued a fresh token.
            if (!data.email || !data.password) {
              setStep(Step.STEP_CONTEXT);
              break;
            }
            if (!data.turnstileToken) {
              setState([{ key: 'turnstileToken', type: ErrorType.INVALID }]);
              break;
            }
            try {
              await authenticateToConnect({
                email: data.email,
                password: data.password,
                turnstileToken: data.turnstileToken,
              });
              redirectToConnect();
            } catch (authError) {
              const isTokenError =
                authError instanceof AuthenticationError &&
                (authError.status === 401 || authError.status === 403);
              if (isTokenError) {
                // Token was rejected — widget will refresh it automatically.
                setState([{ key: 'turnstileToken', type: ErrorType.INVALID }]);
              } else {
                // Non-token failure (network, 5xx) — retrying Turnstile will
                // not help. Surface as a genuine error instead.
                setState({
                  error: t('accountCreation'),
                  source: 'AboutSignUp',
                });
                setStep(Step.STEP_ERROR);
              }
            }
            break;
          }
          default:
            throw new Error(`Unhandled step ${step} in onNext`);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      validators,
      step,
      data,
      trackEvent,
      t,
      signUp,
      verifyAndSignup,
      findExisting,
      isValidVerificationCode,
      executeCaptcha,
    ],
  );

  const onPrevious = useCallback(async () => {
    switch (step) {
      case Step.STEP_TWO_FORM:
        setStep(Step.STEP_ONE_FORM);
        break;
      case Step.STEP_THREE_FORM:
        setStep(Step.STEP_TWO_FORM);
        break;
      case Step.STEP_FOUR_FORM:
        setStep(Step.STEP_THREE_FORM);
        break;
      case Step.STEP_EMAIL_VERIFICATION:
        setStep(Step.STEP_FOUR_FORM);
        break;
      case Step.STEP_FINAL_REDIRECT_LOGIN:
        setStep(Step.STEP_FOUR_FORM);
        break;
      case Step.STEP_TURNSTILE:
        // Allow the user to go back and correct their details. The Turnstile
        // widget will re-issue a fresh token when they advance again.
        setStep(Step.STEP_FOUR_FORM);
        break;
      default:
        throw new Error(`Unhandled step ${step} in onPrevious`);
    }
  }, [step]);

  const registerValidator = useCallback((validator: Validator): Deregister => {
    setValidators(currentValidators => [...currentValidators, validator]);
    return () => {
      setValidators(currentValidators =>
        currentValidators.filter(v => v !== validator),
      );
    };
  }, []);

  // Since there is no next button on the verification step, we call it automatically as soon as the code is the right length
  useEffect(() => {
    if (step !== Step.STEP_EMAIL_VERIFICATION) {
      return;
    }
    if (data.verificationCode?.length === CODE_LENGTH) {
      onNext();
    }
  }, [step, data.verificationCode, onNext]);

  const clearState = useCallback(() => setState(null), []);

  return {
    step,
    state,
    loading,
    onNext,
    onPrevious: previousKey ? onPrevious : undefined,
    registerValidator,
    clearState,
  };
}
